import { createRoot } from 'react-dom/client';
import InvoiceTemplate from '../components/billing/InvoiceTemplate';
import React from 'react';
import { getDocs, collection, query, where, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Helper function to get display text for payment method
const getFizmodText = (paymentMethod: string): string => {
  const paymentMethods: { [key: string]: string } = {
    'CASH': 'Készpénz',
    'CARD': 'Bankkártya',
    'TRANSFER': 'Átutalás',
    'COD': 'Utánvét',
    'ONLINE': 'Online fizetés',
    'CHEQUE': 'Csekk',
    'INSTALLMENT': 'Részletfizetés',
    'COMPENSATION': 'Beszámítás',
    'VOUCHER': 'Utalvány',
    'BARTER': 'Barter',
    'CREDIT': 'Hitel',
    'CLEARING': 'Klíring',
    'FACTORING': 'Faktoring'
  };
  return paymentMethods[paymentMethod] || paymentMethod;
};

class TestInvoiceService {
  private testInvoiceCounter = 1;

  // Generate a unique test invoice number
  generateTestInvoiceNumber(): string {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const counter = String(this.testInvoiceCounter).padStart(3, '0');
    this.testInvoiceCounter++;
    
    return `TESZT-${year}-${month}-${counter}`;
  }

  // Generate and open test invoice in new window
  async generateTestInvoice(invoiceData: any, organizationData?: any): Promise<void> {
    return new Promise((resolve) => {
      console.log('TestInvoiceService received invoiceData:', invoiceData);
      console.log('TestInvoiceService received organizationData:', organizationData);
      
      // Use the invoice number from invoiceData if available, otherwise generate one
      const testInvoiceNumber = invoiceData.invoiceNumber || this.generateTestInvoiceNumber();
      
      // Create new window for the invoice
      const printWindow = window.open('', '_blank', 'width=1024,height=768,scrollbars=yes,resizable=yes');
      
      if (!printWindow) {
        throw new Error('Popup blokkolva! Engedélyezze a popup ablakokat a teszt számlához.');
      }

      let netTotal = 0;
      let taxTotal = 0;
      let grossTotal = 0;

      const itemsHtml = invoiceData.items?.map((item: any) => {
        const itemNet = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
        const itemTax = itemNet * (parseFloat(item.taxRate || 0) / 100);
        const itemGross = itemNet + itemTax;
        
        netTotal += itemNet;
        taxTotal += itemTax;
        grossTotal += itemGross;

        const formatCurrency = (amount: number) => 
          new Intl.NumberFormat('hu-HU').format(Math.round(amount)) + ' Ft';

        return `
          <tr>
            <td>${item.name || 'Tétel'}</td>
            <td class="text-center">${item.quantity || 0}</td>
            <td class="text-center">${item.unit || 'db'}</td>
            <td class="text-right">${formatCurrency(item.unitPrice || 0)}</td>
            <td class="text-center">${item.taxRate || 0}%</td>
            <td class="text-right">${formatCurrency(itemNet)}</td>
            <td class="text-right">${formatCurrency(itemTax)}</td>
            <td class="text-right">${formatCurrency(itemGross)}</td>
          </tr>
        `;
      }).join('') || '';

      const formatCurrency = (amount: number) => 
        new Intl.NumberFormat('hu-HU').format(Math.round(amount)) + ' Ft';

      const htmlContent = `
        <!DOCTYPE html>
        <html lang="hu">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Teszt Számla - ${testInvoiceNumber}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              @page {
                margin: 0.5in;
              }
              body {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              .no-print {
                display: none !important;
              }
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f5f5f5;
            }
            
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              padding: 2rem;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              border-radius: 8px;
            }
            
            .dev-indicator {
              position: absolute;
              top: 10px;
              right: 10px;
              background: rgba(59, 130, 246, 0.1);
              color: #2563eb;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 0.8rem;
              font-weight: 500;
              z-index: 1;
            }
            
            .content {
              position: relative;
              z-index: 1;
            }
            
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #3b82f6;
              color: white;
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-size: 16px;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              transition: background-color 0.2s;
            }
            
            .print-button:hover {
              background: #2563eb;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 1rem 0;
            }
            
            th, td {
              border: 1px solid #d1d5db;
              padding: 8px 12px;
              text-align: left;
            }
            
            th {
              background-color: #f9fafb;
              font-weight: 600;
            }
            
            .text-right {
              text-align: right;
            }
            
            .text-center {
              text-align: center;
            }
            
            .font-bold {
              font-weight: bold;
            }
            
            .text-red-600 {
              color: #dc2626;
            }
            
            .bg-red-100 {
              background-color: #fee2e2;
              padding: 0.5rem 1rem;
              border-radius: 4px;
              display: inline-block;
            }
            
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 2rem;
              margin: 2rem 0;
            }
            
            .border-b {
              border-bottom: 1px solid #d1d5db;
              padding-bottom: 0.5rem;
              margin-bottom: 1rem;
            }
            
            .totals-table {
              margin-left: auto;
              width: 300px;
            }
            
            .totals-table td {
              border: none;
              padding: 0.5rem 0;
            }
            
            .totals-table .total-row {
              border-top: 2px solid #374151;
              font-weight: bold;
              font-size: 1.1rem;
            }
          </style>
        </head>
        <body>
          <button class="print-button no-print" onclick="window.print()">🖨️ Nyomtatás</button>
          
          <div class="invoice-container">
            <div class="dev-indicator no-print">DEV</div>
            
            <div class="content">
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2rem;">
                <div>
                  <h1 style="font-size: 2rem; font-weight: bold; margin: 0 0 1rem 0;">SZÁMLA</h1>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 1.2rem; font-weight: 600; margin-bottom: 0.5rem;">Számlaszám: ${testInvoiceNumber}</div>
                  <div>Kiállítás dátuma: ${new Date().toLocaleDateString('hu-HU')}</div>
                  <div>Teljesítés dátuma: ${new Date().toLocaleDateString('hu-HU')}</div>
                  <div>Fizetési határidő: ${new Date(Date.now() + (invoiceData.paymentDeadline || 8) * 24 * 60 * 60 * 1000).toLocaleDateString('hu-HU')}</div>
                </div>
              </div>

              <div class="grid-2">
                <div>
                  <h3 class="font-bold border-b">Eladó (Kiállító):</h3>
                  <div><strong>${organizationData?.name || 'Szervezet Neve'}</strong></div>
                  <div>${organizationData?.address || 'Cím nem megadva'}</div>
                  <div>Adószám: ${organizationData?.taxNumber || 'Nincs megadva'}</div>
                  <div>Email: ${organizationData?.email || 'nincs@megadva.hu'}</div>
                </div>
                
                <div>
                  <h3 class="font-bold border-b">Vevő:</h3>
                  <div><strong>${invoiceData.buyer?.name || 'Vevő neve'}</strong></div>
                  ${invoiceData.buyer?.address ? `
                    <div>${invoiceData.buyer.address.street || ''}</div>
                    <div>${invoiceData.buyer.address.postalCode || ''} ${invoiceData.buyer.address.city || ''}</div>
                    <div>${invoiceData.buyer.address.country || ''}</div>
                  ` : ''}
                  ${invoiceData.buyer?.taxNumber ? `<div>Adószám: ${invoiceData.buyer.taxNumber}</div>` : ''}
                  ${invoiceData.buyer?.email ? `<div>Email: ${invoiceData.buyer.email}</div>` : ''}
                </div>
              </div>

              <div style="margin: 2rem 0;">
                <h3 class="font-bold border-b">Tételek:</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Megnevezés</th>
                      <th class="text-center">Menny.</th>
                      <th class="text-center">Egység</th>
                      <th class="text-right">Nettó ár</th>
                      <th class="text-center">ÁFA %</th>
                      <th class="text-right">Nettó érték</th>
                      <th class="text-right">ÁFA érték</th>
                      <th class="text-right">Bruttó érték</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsHtml}
                  </tbody>
                </table>
              </div>

              <div style="display: flex; justify-content: flex-end; margin: 2rem 0;">
                <table class="totals-table">
                  <tr>
                    <td>Nettó összeg:</td>
                    <td class="text-right font-bold">${formatCurrency(netTotal)}</td>
                  </tr>
                  <tr>
                    <td>ÁFA összeg:</td>
                    <td class="text-right font-bold">${formatCurrency(taxTotal)}</td>
                  </tr>
                  <tr class="total-row">
                    <td>Fizetendő összeg:</td>
                    <td class="text-right">${formatCurrency(grossTotal)}</td>
                  </tr>
                </table>
              </div>

              <div class="grid-2">
                <div>
                  <h3 class="font-bold border-b">Fizetési információk:</h3>
                  <div>Fizetési mód: ${getFizmodText(invoiceData.paymentMethod) || 'Átutalás'}</div>
                  <div>Fizetési határidő: ${invoiceData.paymentDeadline || 8} nap</div>
                  <div>Pénznem: ${invoiceData.currency || 'HUF'}</div>
                </div>
                
                ${invoiceData.notes ? `
                  <div>
                    <h3 class="font-bold border-b">Megjegyzések:</h3>
                    <div>${invoiceData.notes}</div>
                  </div>
                ` : ''}
              </div>

              <div style="text-align: center; margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #d1d5db; color: #6b7280; font-size: 0.9rem;">
                <div>Generálva: ${new Date().toLocaleString('hu-HU')}</div>
                <div>MosógépGuru CRM - Számla generátor</div>
              </div>
            </div>
          </div>

          <script>
            // Auto-focus the window and optionally auto-print
            window.focus();
            
            // Optional: Auto-print dialog after a short delay
            // setTimeout(() => window.print(), 1000);
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Focus the new window
      printWindow.focus();
      
      resolve();
    });
  }
}

export const testInvoiceService = new TestInvoiceService();

// Generate unique test invoice number (normal format for developer mode)
export const generateTestInvoiceNumber = async (organizationId: string): Promise<string> => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  
  // Get existing DEV invoices for this month to determine next number
  const devInvoicesQuery = query(
    collection(db, 'invoices'),
    where('organizationId', '==', organizationId),
    where('status', '==', 'DEV')
  );
  
  const devInvoicesSnapshot = await getDocs(devInvoicesQuery);
  
  // Filter for current month and count MG- prefixed invoices
  const currentMonthDevInvoices = devInvoicesSnapshot.docs.filter(doc => {
    const invoiceNumber = doc.data().invoiceNumber;
    return invoiceNumber && invoiceNumber.startsWith(`MG-${year}-${month}-`);
  });
  
  const nextNumber = currentMonthDevInvoices.length + 1;
  
  // Generate MG prefixed invoice number for developer mode
  return `MG-${year}-${month}-${nextNumber.toString().padStart(3, '0')}`;
};

// Generate test invoice with real data from draft
export const generateTestDocumentFromDraft = async (
  draftId: string,
  organizationId: string
): Promise<any> => {
  try {
    console.log('Generating test document from draft:', draftId, 'for organization:', organizationId);

    // Import getDraftInvoice function here to avoid circular imports
    const { getDraftInvoice } = await import('../lib/services/billingService');
    
    // Get the draft invoice data
    const draftData = await getDraftInvoice(draftId, organizationId);
    if (!draftData) {
      throw new Error('Draft invoice not found');
    }

    // Get organization settings
    const orgQuery = query(
      collection(db, 'billingSettings'),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      limit(1)
    );
    
    const orgSnapshot = await getDocs(orgQuery);
    let organizationData = null;
    
    if (!orgSnapshot.empty) {
      organizationData = orgSnapshot.docs[0].data();
    }

    // Also get organization info from organizations collection
    const { getDoc, doc } = await import('firebase/firestore');
    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    const organizationInfo = orgDoc.exists() ? orgDoc.data() : null;

    // Generate DEV invoice number (not from szamlazz.hu)
    const devInvoiceNumber = await generateTestInvoiceNumber(organizationId);

    // Calculate totals from real draft data
    let netAmount = 0;
    let grossAmount = 0;
    
    if (draftData.items && Array.isArray(draftData.items)) {
      draftData.items.forEach((item: any) => {
        const itemNet = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
        netAmount += itemNet;
        grossAmount += itemNet * (1 + parseFloat(item.taxRate || 0) / 100);
      });
    }

    // Apply automatic payment deadline logic based on payment method
    let paymentDeadline = draftData.paymentDeadline || 8;
    const paymentMethod = draftData.paymentMethod || 'TRANSFER';
    
    // Automatic payment deadline for immediate payment methods
    if (['CASH', 'CARD', 'ONLINE', 'COD'].includes(paymentMethod)) {
      paymentDeadline = 0;
    }

    // Use real draft data with DEV invoice number
    const realInvoiceData = {
      invoiceNumber: devInvoiceNumber, // Our generated number, not szamlazz.hu
      invoiceType: draftData.invoiceType || 'INVOICE',
      issueDate: new Date(),
      dueDate: new Date(Date.now() + paymentDeadline * 24 * 60 * 60 * 1000),
      paymentMethod: paymentMethod,
      paymentDeadline: paymentDeadline, // Add this field
      currency: draftData.currency || 'HUF',
      
      // Real buyer data from draft
      buyer: {
        name: draftData.buyerName || 'Ismeretlen vevő',
        taxNumber: draftData.buyerTaxNumber || '',
        address: draftData.buyerAddress ? {
          street: draftData.buyerAddress.split(',')[0]?.trim() || '',
          city: draftData.buyerAddress.split(',')[1]?.trim() || '',
          postalCode: draftData.buyerAddress.split(',')[2]?.trim() || '',
          country: 'Magyarország'
        } : undefined,
        email: draftData.buyerEmail || '',
        phone: draftData.buyerPhone || ''
      },
      
      // Real items from draft
      items: draftData.items || [],
      
      // Organization data
      organization: organizationData ? {
        name: organizationData.sellerData?.name || organizationData.companyName || 'Cégnév',
        taxNumber: organizationData.sellerData?.taxNumber || '00000000-0-00',
        address: organizationData.sellerData?.address || 'Cím utca 1.',
        phone: organizationData.sellerData?.phone || '+36 1 000 0000',
        email: organizationData.sellerData?.email || 'info@cegnev.hu',
        bankAccount: organizationData.sellerData?.bankAccount || '12345678-12345678-12345678'
      } : {
        name: 'Mosógép Guru Kft.',
        taxNumber: '00000000-0-00',
        address: 'Minta utca 1., Budapest, 1111',
        phone: '+36 1 000 0000',
        email: 'info@mosogepguru.hu',
        bankAccount: '12345678-12345678-12345678'
      },
      
      notes: draftData.notes || 'Fejlesztői módban generált bizonylat.',
      
      // Calculate real totals
      netAmount: Math.round(netAmount),
      grossAmount: Math.round(grossAmount),
      
      // Developer mode indicators
      isDeveloperMode: true,
      testInvoice: true
    };

    console.log('Generated test invoice data from real draft:', realInvoiceData);
    return realInvoiceData;

  } catch (error) {
    console.error('Error generating test document from draft:', error);
    throw new Error(`Failed to generate test document from draft: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Keep the old function for backward compatibility with sample data
export const generateTestDocument = async (
  organizationId: string, 
  invoiceType: 'INVOICE' | 'PROFORMA' = 'INVOICE'
): Promise<any> => {
  try {
    console.log('Generating test document with sample data for organization:', organizationId);

    // Get organization settings
    const orgQuery = query(
      collection(db, 'billingSettings'),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true),
      limit(1)
    );
    
    const orgSnapshot = await getDocs(orgQuery);
    let organizationData = null;
    
    if (!orgSnapshot.empty) {
      organizationData = orgSnapshot.docs[0].data();
    }

    // Generate test invoice number
    const testInvoiceNumber = await generateTestInvoiceNumber(organizationId);

    // Create sample invoice data
    const sampleInvoiceData = {
      invoiceNumber: testInvoiceNumber,
      invoiceType: invoiceType,
      issueDate: new Date(),
      dueDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000), // 8 days from now
      paymentMethod: 'Átutalás',
      currency: 'HUF',
      
      // Sample buyer data
      buyer: {
        name: 'Minta Vevő Kft.',
        taxNumber: '12345678-1-23',
        address: {
          street: 'Minta utca 123.',
          city: 'Budapest',
          postalCode: '1234',
          country: 'Magyarország'
        },
        email: 'minta@example.com',
        phone: '+36 1 234 5678'
      },
      
      // Sample items
      items: [
        {
          name: 'Mosógép javítás',
          quantity: 1,
          unit: 'db',
          unitPrice: 15000,
          taxRate: 27,
          comment: 'Kijavított alkatrészek cseréje'
        },
        {
          name: 'Alkatrész csere',
          quantity: 2,
          unit: 'db',
          unitPrice: 5000,
          taxRate: 27,
          comment: 'Eredeti gyári alkatrészek'
        }
      ],
      
      // Organization data
      organization: organizationData ? {
        name: organizationData.sellerData?.name || organizationData.companyName || 'Cégnév',
        taxNumber: organizationData.sellerData?.taxNumber || '00000000-0-00',
        address: organizationData.sellerData?.address || 'Cím utca 1.',
        phone: organizationData.sellerData?.phone || '+36 1 000 0000',
        email: organizationData.sellerData?.email || 'info@cegnev.hu',
        bankAccount: organizationData.sellerData?.bankAccount || '12345678-12345678-12345678'
      } : {
        name: 'Mosógép Guru Kft.',
        taxNumber: '00000000-0-00',
        address: 'Minta utca 1., Budapest, 1111',
        phone: '+36 1 000 0000',
        email: 'info@mosogepguru.hu',
        bankAccount: '12345678-12345678-12345678'
      },
      
      notes: 'Fejlesztői módban generált bizonylat.',
      
      // Calculate totals
      netAmount: 25000, // 15000 + 2*5000
      grossAmount: 31750, // 25000 * 1.27
      
      // Developer mode indicators
      isDeveloperMode: true,
      testInvoice: true
    };

    console.log('Generated test invoice data:', sampleInvoiceData);
    return sampleInvoiceData;

  } catch (error) {
    console.error('Error generating test document:', error);
    throw new Error(`Failed to generate test document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}; 